import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { BranchInfo } from '../../sdk/sdk';
import { BranchService } from '../../services/branch.service';

@Component({
  selector: 'app-branch-selector',
  imports: [NgSelectComponent, ReactiveFormsModule, CommonModule],
  templateUrl: './branch-selector.component.html',
})
export class BranchSelectorComponent implements OnInit, OnDestroy {
  private readonly branchService = inject(BranchService);
  private readonly destroyRef = inject(DestroyRef);

  @Output() branchSelected = new EventEmitter<BranchInfo>();

  getBranchesSubscription: Subscription = new Subscription();
  branches: BranchInfo[] = [];
  branchData = new FormGroup({
    selectedBranch: new FormControl<string | null>(null),
  });

  constructor() {
    this.branchData.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        const selectedBranch = this.branches.find(
          (branch) => branch.id === value.selectedBranch
        );
        if (!selectedBranch) return;
        this.branchSelected.emit(selectedBranch);
      });
  }

  private syncFormFromServiceSelection(selected: BranchInfo | null): void {
    if (selected?.id && this.branches.length > 0) {
      this.branchData.patchValue(
        { selectedBranch: selected.id },
        { emitEvent: false }
      );
    }
  }

  ngOnInit() {
    this.getBranchesSubscription = this.branchService.getBranches().subscribe({
      next: (branches) => {
        this.branches = branches;
        this.branchService
          .getSelectedBranch()
          .pipe(take(1))
          .subscribe((selected) => this.syncFormFromServiceSelection(selected));
      },
      error: (error) => {
        console.error('Error fetching branches:', error);
      },
    });

    this.branchService
      .getSelectedBranch()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((selected): selected is BranchInfo => selected !== null)
      )
      .subscribe((selected) => this.syncFormFromServiceSelection(selected));
  }

  resetSelection(): void {
    this.branchData.patchValue({ selectedBranch: null }, { emitEvent: false });
  }

  ngOnDestroy(): void {
    this.getBranchesSubscription.unsubscribe();
  }
}
